import { useState } from 'react';
import { 
  MessageCircle, 
  X, 
  Filter,
  CheckCircle,
  Clock,
  ChevronRight
} from 'lucide-react';
import { Button } from '../ui/button';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export const CommentsPanel = ({
  isOpen,
  onClose,
  comments,
  onSelectComment,
  onNavigateToComment,
  currentUserId,
}) => {
  const [filter, setFilter] = useState('all'); // 'all', 'open', 'resolved'

  if (!isOpen) return null;

  const filteredComments = comments.filter((c) => {
    if (filter === 'open') return !c.resolved;
    if (filter === 'resolved') return c.resolved;
    return true;
  });

  const openCount = comments.filter((c) => !c.resolved).length;
  const resolvedCount = comments.filter((c) => c.resolved).length;

  return (
    <div 
      className="fixed right-0 top-14 bottom-0 w-80 bg-white border-l border-gray-200 shadow-lg z-40 flex flex-col"
      data-testid="comments-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <MessageCircle size={18} className="text-gray-600" />
          <span className="font-semibold text-gray-900">Comments</span>
          <span className="text-sm text-gray-500">({comments.length})</span>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
          <X size={18} />
        </Button>
      </div>

      {/* Filter tabs */}
      <div className="flex px-4 py-2 gap-2 border-b border-gray-100">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
            filter === 'all'
              ? 'bg-gray-900 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          All ({comments.length})
        </button>
        <button
          onClick={() => setFilter('open')}
          className={`px-3 py-1.5 text-sm rounded-full transition-colors flex items-center gap-1 ${
            filter === 'open'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Clock size={12} />
          Open ({openCount})
        </button>
        <button
          onClick={() => setFilter('resolved')}
          className={`px-3 py-1.5 text-sm rounded-full transition-colors flex items-center gap-1 ${
            filter === 'resolved'
              ? 'bg-green-500 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <CheckCircle size={12} />
          Resolved ({resolvedCount})
        </button>
      </div>

      {/* Comments list */}
      <div className="flex-1 overflow-y-auto">
        {filteredComments.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <MessageCircle size={40} className="mb-2 opacity-50" />
            <p className="text-sm">No comments yet</p>
            <p className="text-xs mt-1">Click on the canvas to add a comment</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredComments.map((comment) => {
              const isOwn = comment.user_id === currentUserId;
              const replyCount = comment.replies?.length || 0;

              return (
                <div
                  key={comment.id}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors group"
                  onClick={() => onSelectComment(comment)}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0 ${
                        isOwn ? 'bg-blue-500' : 'bg-purple-500'
                      }`}
                    >
                      {comment.user_name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {comment.user_name}
                        </span>
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {formatDate(comment.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                        {comment.content}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        {comment.resolved ? (
                          <span className="flex items-center gap-1 text-xs text-green-600">
                            <CheckCircle size={12} />
                            Resolved
                          </span>
                        ) : replyCount > 0 ? (
                          <span className="text-xs text-gray-500">
                            {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                          </span>
                        ) : null}
                        
                        {comment.reactions?.length > 0 && (
                          <div className="flex gap-1">
                            {[...new Set(comment.reactions.map(r => r.emoji))].slice(0, 3).map((emoji, i) => (
                              <span key={i} className="text-xs">{emoji}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigateToComment(comment);
                      }}
                      className="p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-200 rounded transition-all"
                      title="Go to comment"
                    >
                      <ChevronRight size={16} className="text-gray-400" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
